/**
 * Production Monitoring & Error Tracking
 * 
 * Provides centralized monitoring, error tracking, and performance metrics
 * for production deployment readiness.
 */

import { supabase } from '@/integrations/supabase/client';

// Environment detection
export const isProduction = import.meta.env.PROD;
export const isDevelopment = import.meta.env.DEV;

// ============= ERROR TRACKING =============

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  leagueId?: string;
  additionalData?: Record<string, any>;
}

export class ErrorTracker {
  /**
   * Track and log errors with context
   */
  static captureError(error: Error, context?: ErrorContext) {
    const errorData = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      environment: isProduction ? 'production' : 'development',
      ...context
    };

    // Log to console in development
    if (isDevelopment) {
      console.error('[Error Captured]', errorData);
    }

    // Log to database for production tracking
    if (isProduction) {
      this.logToDatabase(errorData).catch(err => {
        console.error('Failed to log error to database:', err);
      });
    }

    // TODO: Add Sentry integration when SENTRY_DSN is configured
    // if (import.meta.env.VITE_SENTRY_DSN) {
    //   Sentry.captureException(error, { contexts: { custom: context } });
    // }
  }

  /**
   * Log error to Supabase for tracking
   * Note: Requires error_logs table - see docs/MONITORING_SETUP.md
   */
  private static async logToDatabase(errorData: any) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // TODO: Create error_logs table first (see MONITORING_SETUP.md)
      // Temporarily disabled until table is created
      console.error('[Error Log]', errorData);
      
      // await supabase.from('error_logs').insert({
      //   user_id: user?.id,
      //   error_type: 'client_error',
      //   error_message: errorData.message,
      //   error_stack: errorData.stack,
      //   context: errorData,
      //   created_at: new Date().toISOString()
      // });
    } catch (err) {
      // Fail silently to not disrupt user experience
      console.warn('Error logging failed:', err);
    }
  }

  /**
   * Capture warning-level issues
   */
  static captureWarning(message: string, context?: ErrorContext) {
    if (isDevelopment) {
      console.warn('[Warning]', message, context);
    }
  }

  /**
   * Capture informational messages
   */
  static captureInfo(message: string, data?: any) {
    if (isDevelopment) {
      console.info('[Info]', message, data);
    }
  }
}

// ============= PERFORMANCE MONITORING =============

export class PerformanceMonitor {
  private static measurements = new Map<string, number>();

  /**
   * Start a performance measurement
   */
  static startMeasure(label: string) {
    this.measurements.set(label, performance.now());
  }

  /**
   * End a performance measurement and log duration
   */
  static endMeasure(label: string): number {
    const startTime = this.measurements.get(label);
    if (!startTime) {
      console.warn(`No start time found for measurement: ${label}`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.measurements.delete(label);

    if (isDevelopment) {
      console.log(`[Performance] ${label}: ${duration.toFixed(2)}ms`);
    }

    // Log slow operations in production
    if (isProduction && duration > 1000) {
      ErrorTracker.captureWarning(`Slow operation: ${label} took ${duration}ms`);
    }

    return duration;
  }

  /**
   * Measure async operation
   */
  static async measureAsync<T>(
    label: string, 
    operation: () => Promise<T>
  ): Promise<T> {
    this.startMeasure(label);
    try {
      const result = await operation();
      this.endMeasure(label);
      return result;
    } catch (error) {
      this.endMeasure(label);
      throw error;
    }
  }

  /**
   * Get Core Web Vitals
   */
  static getCoreWebVitals() {
    if (typeof window === 'undefined') return null;

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    return {
      // Largest Contentful Paint
      lcp: navigation?.loadEventEnd - navigation?.fetchStart,
      // First Input Delay (approximation)
      fid: navigation?.responseStart - navigation?.requestStart,
      // Cumulative Layout Shift (needs separate observer)
      cls: 0, // TODO: Implement CLS tracking
      // Time to First Byte
      ttfb: navigation?.responseStart - navigation?.fetchStart
    };
  }
}

// ============= DATABASE MONITORING =============

export class DatabaseMonitor {
  /**
   * Monitor slow queries
   */
  static async monitorQuery<T>(
    queryName: string,
    queryFn: () => Promise<T>
  ): Promise<T> {
    return PerformanceMonitor.measureAsync(`DB: ${queryName}`, queryFn);
  }

  /**
   * Check database health
   */
  static async checkHealth(): Promise<{ healthy: boolean; latency: number }> {
    const start = performance.now();
    
    try {
      const { error } = await supabase.from('profiles').select('id').limit(1);
      const latency = performance.now() - start;
      
      return {
        healthy: !error,
        latency
      };
    } catch (error) {
      return {
        healthy: false,
        latency: performance.now() - start
      };
    }
  }
}

// ============= ANALYTICS TRACKING =============

export class AnalyticsTracker {
  /**
   * Track custom events - logs in dev, sends to Vercel Analytics in production
   */
  static trackEvent(eventName: string, properties?: Record<string, any>) {
    if (isDevelopment) {
      console.log('[Analytics]', eventName, properties);
      return;
    }
    // Use Vercel Analytics custom events if available
    try {
      const va = (window as any).va;
      if (va) {
        va('event', { name: eventName, ...properties });
      }
    } catch {
      // silently fail
    }
  }

  // PLG-specific tracking helpers
  static trackSignup(method: string) {
    this.trackEvent('signup_completed', { method });
  }

  static trackFirstLeagueCreated(sport: string) {
    this.trackEvent('first_league_created', { sport });
  }

  static trackFirstGameScored(leagueId: string) {
    this.trackEvent('first_game_scored', { league_id: leagueId });
  }

  static trackPublicUrlShared(leagueId: string) {
    this.trackEvent('public_url_shared', { league_id: leagueId });
  }

  static trackPlanUpgraded(plan: string) {
    this.trackEvent('plan_upgraded', { plan });
  }

  /**
   * Track page views
   */
  static trackPageView(path: string, title?: string) {
    if (isDevelopment) {
      console.log('[Analytics] Page View:', path, title);
    }
  }

  /**
   * Track user actions
   */
  static trackUserAction(action: string, category: string, label?: string) {
    this.trackEvent(`${category}_${action}`, { label });
  }

  /**
   * Identify user (no-op without PostHog)
   */
  static async identifyUser(userId: string, properties?: Record<string, any>) {
    if (isDevelopment) {
      console.log('[Analytics] Identify:', userId, properties);
    }
  }

  /**
   * Reset user identification (no-op without PostHog)
   */
  static async resetUser() {
    if (isDevelopment) {
      console.log('[Analytics] Reset user');
    }
  }
}

// ============= FEATURE FLAG SYSTEM =============

export class FeatureFlags {
  private static flags = new Map<string, boolean>();
  
  // Default feature flag values - features default to disabled unless specified
  private static defaults: Record<string, boolean> = {};

  /**
   * Check if a feature is enabled
   */
  static isEnabled(featureName: string): boolean {
    // Check localStorage first for dev overrides
    if (isDevelopment && typeof localStorage !== 'undefined') {
      const override = localStorage.getItem(`feature_${featureName}`);
      if (override !== null) {
        return override === 'true';
      }
    }

    // Check in-memory cache
    if (this.flags.has(featureName)) {
      return this.flags.get(featureName)!;
    }

    // Check defaults registry
    if (featureName in this.defaults) {
      return this.defaults[featureName];
    }

    // Default to enabled in production for unknown flags
    return isProduction;
  }

  /**
   * Enable a feature (dev only)
   */
  static enable(featureName: string) {
    if (isDevelopment) {
      localStorage.setItem(`feature_${featureName}`, 'true');
      this.flags.set(featureName, true);
    }
  }

  /**
   * Disable a feature (dev only)
   */
  static disable(featureName: string) {
    if (isDevelopment) {
      localStorage.setItem(`feature_${featureName}`, 'false');
      this.flags.set(featureName, false);
    }
  }
}

// ============= HEALTH CHECK =============

export interface HealthCheckResult {
  status: 'healthy' | 'degraded' | 'down';
  checks: {
    database: { healthy: boolean; latency: number };
    auth: { healthy: boolean };
  };
  timestamp: string;
}

export async function performHealthCheck(): Promise<HealthCheckResult> {
  const dbHealth = await DatabaseMonitor.checkHealth();
  
  let authHealthy = false;
  try {
    const { data } = await supabase.auth.getSession();
    authHealthy = true;
  } catch {
    authHealthy = false;
  }

  const allHealthy = dbHealth.healthy && authHealthy;
  const status: HealthCheckResult['status'] = 
    allHealthy ? 'healthy' : 
    (dbHealth.healthy || authHealthy) ? 'degraded' : 
    'down';

  return {
    status,
    checks: {
      database: dbHealth,
      auth: { healthy: authHealthy }
    },
    timestamp: new Date().toISOString()
  };
}

// Export convenient aliases
export const captureError = ErrorTracker.captureError.bind(ErrorTracker);
export const trackEvent = AnalyticsTracker.trackEvent.bind(AnalyticsTracker);
export const measurePerformance = PerformanceMonitor.measureAsync.bind(PerformanceMonitor);
